import { TableBody, Card, CardContent, Popover } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import React, { Fragment, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import type { Data } from '~/services/data-service';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';

const useStyles = makeStyles()({
  tableContainer: {
    overflowX: 'auto',
  },
  inputHeader: {
    minWidth: '7rem',
    padding: '6px',
  },
  selectedEntry: {
    backgroundColor: '#eaeaea',
  },
  yearCell: {
    fontWeight: 'bold',
    verticalAlign: 'top',
    paddingTop: '18px',
  },
  deleteButton: {
    padding: '5px',
  },
  popover: {},
  paper: {},
  card: {
    minWidth: 275
  },
});

interface ViewTableProps {
  emissions: string[];
  dimensions: string[];
  data: Data[];
}

const ViewTable: React.FC<ViewTableProps> = ({ emissions, dimensions, data }) => {
  const { classes } = useStyles();

  const [emissionData, setEmissionData] = useState<{
    emissionYear: string;
    value: string;
    comment: string;
    nk_explanation: string;
    allocation_by_ipcc: string;
    allocation_by_party: string;
  } | undefined | null>(null);

  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement | null>(null)

  // Convert intToString function
  const intToString = (value: number): string => {
    if (Math.abs(value) < 1) {
      return value.toFixed(2);
    } else if (Math.abs(value) < 10) {
      return value.toFixed(2);
    } else if (Math.abs(value) < 100) {
      return value.toFixed(1);
    } else if (Math.abs(value) < 1000) {
      return value.toFixed(0);
    } else if (Math.abs(value) < 10000) {
      return (value / 1000).toFixed(2) + 'k';
    } else if (Math.abs(value) < 100000) {
      return (value / 1000).toFixed(1) + 'k';
    } else if (Math.abs(value) < 1000000) {
      return (value / 1000).toFixed(0) + 'k';
    } else {
      return (value / 1000000).toFixed(0) + 'm';
    }
  };

  // Render data row function
  const renderDataRow = (data: Data, index: number) => {
    const getContent = (dimension: keyof Data) => (<>{data[dimension]}</>);
    const getValue = (emission: string) =>
      data.dataValues.find((x) => x.emissionYear === emission)
        ? data.dataValues.find((x) => x.emissionYear === emission)!.value
        : '';
    const toFixed = (value: string) =>
      Number.parseFloat(value.replace(',', '.')) ? intToString(Number.parseFloat(value.replace(',', '.'))) : value;

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement, MouseEvent>, emission: string) => {
      setPopoverAnchorEl(event.currentTarget);
      setEmissionData(data?.dataValues?.find((x) => x.emissionYear === emission));
    };

    const handlePopoverClose = () => {
      setPopoverAnchorEl(null);
      setEmissionData(null);
    };

    return (
      <TableRow key={index}>
        {dimensions.map((dimension: string) => (
          <TableCell key={dimension} size="small">
            {getContent(dimension.toLowerCase() as keyof Data)}
          </TableCell>
        ))}
        {emissions.map((emission: string) => (
            <Fragment key={`fragment-${emission}`}>
              <TableCell
                  key={emission}
                  size="small"
                  aria-owns={popoverAnchorEl ? `mouse-over-popover-${emission}` : undefined}
                  aria-haspopup="true"
                  onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => handlePopoverOpen(e, emission)}
              >
                {toFixed(getValue(emission))}
              </TableCell>
            </Fragment>
        ))}
        {getPopover(handlePopoverClose)}
      </TableRow>
    );
  };


  const getPopover = (handlePopoverClose: () => void) =>  {

    if (!emissionData?.value) {
      return <></>;
    }

    return emissionData && (
        <Popover
            id={`mouse-over-popover-${emissionData.emissionYear}`}
            key={`mouse-over-popover-${emissionData.emissionYear}`}
            className={classes.popover}
            classes={{
              paper: classes.paper,
            }}
            open={!!popoverAnchorEl}
            anchorEl={popoverAnchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            onClose={handlePopoverClose}
        >
          <Card className={classes.card} variant="outlined">
            <CardContent>
              <Typography variant="h6">
                Värde
              </Typography>
              <Typography variant="caption">
                {emissionData.value}
              </Typography>
              <Divider style={{marginTop: '8px', marginBottom: '8px'}} variant="fullWidth"/>
              <Typography variant="h6">
                Kommentar
              </Typography>
              <Typography variant="caption">
                {emissionData.comment}
              </Typography>
              <Divider style={{marginTop: '8px', marginBottom: '8px'}} variant="fullWidth"/>
              <Typography variant="h6">
                NK Explanation
              </Typography>
              <Typography variant="caption">
                {emissionData.nk_explanation}
              </Typography>
              <Divider style={{marginTop: '8px', marginBottom: '8px'}} variant="fullWidth"/>
              <Typography variant="h6">
                Allocation_by_ipcc
              </Typography>
              <Typography variant="caption">
                {emissionData.allocation_by_ipcc}
              </Typography>
              <Divider style={{marginTop: '8px', marginBottom: '8px'}} variant="fullWidth"/>
              <Typography variant="h6">
                Allocation_by_party
              </Typography>
              <Typography variant="caption">
                {emissionData.allocation_by_party}
              </Typography>
            </CardContent>
          </Card>
        </Popover>
    );
  }

  return (
    <Paper className={classes.tableContainer}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {dimensions.map((dimension) => (
              <TableCell key={dimension} size="small">
                {dimension}
              </TableCell>
            ))}
            {emissions.map((emission) => (
              <TableCell key={emission} size="small">
                {emission}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>{data.map((data, index) => renderDataRow(data, index))}</TableBody>
      </Table>
    </Paper>
  );
};

export default ViewTable;
